import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { ParticipantRole } from '@gps-hunt/shared';

interface RoleSelectScreenProps {
  onSelect: (role: ParticipantRole) => void;
  isLoading?: boolean;
}

/**
 * Role selection screen - Choose hider or hunter
 */
export const RoleSelectScreen: React.FC<RoleSelectScreenProps> = ({
  onSelect,
  isLoading = false,
}) => {
  const navigate = useNavigate();

  const roles = [
    {
      role: ParticipantRole.HIDER,
      backgroundImage: '/hider-bg.png',
      title: 'HIDER',
      description: 'Hide pins for others to find',
      detail: "You'll have a map to place pins",
    },
    {
      role: ParticipantRole.HUNTER,
      backgroundImage: '/hunter-bg.png',
      title: 'HUNTER',
      description: 'Navigate using only a compass',
      detail: 'Race to find all hidden pins',
    },
    {
      role: ParticipantRole.SPECTATOR,
      backgroundImage: '',
      title: '👁️ SPECTATOR',
      description: 'Watch the hunt live on a map',
      detail: 'No GPS required',
    },
  ];

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 p-4">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-text-secondary hover:text-text-primary transition-colors"
        >
          <ChevronLeft className="w-6 h-6" />
          <span>Back</span>
        </button>
      </div>

      {/* Content - scrollable */}
      <div className="flex-1 overflow-auto flex flex-col items-center p-6 pt-2 pb-safe-bottom space-y-6">
        <h1 className="text-2xl font-bold text-text-primary">CHOOSE YOUR ROLE</h1>

        <div className="w-full max-w-sm space-y-4">
          {roles.map(({ role, backgroundImage, title, description, detail }) => (
            <button
              key={role}
              onClick={() => onSelect(role)}
              disabled={isLoading}
              className="w-full aspect-[3/2] rounded-2xl border-2 border-surface-light hover:border-primary transition-all active:scale-98 disabled:opacity-50 text-left group overflow-hidden relative"
              style={backgroundImage ? {
                backgroundImage: `url(${backgroundImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              } : {
                background: 'linear-gradient(135deg, rgba(99,102,241,0.15), rgba(139,92,246,0.15))',
              }}
            >
              {/* Content */}
              <div className="absolute inset-0 flex flex-col items-center justify-center p-6">
                <h2 className="text-xl font-bold text-text-primary mb-1">{title}</h2>
                <p className="text-text-secondary text-sm">{description}</p>
                <p className="text-text-secondary text-xs opacity-80">{detail}</p>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default RoleSelectScreen;
